// First Created - 26 May 2026
// Author - Atul Pandey
// Purpose - RTK Query API slice for User and Coach Session QNA responses

import {
    createSelector,
    createEntityAdapter,
} from '@reduxjs/toolkit'
import { apiSlice } from '@/app/api/apiSlice'

const userSessionQNAResponsesAdapter = createEntityAdapter({})

const initialState = userSessionQNAResponsesAdapter.getInitialState()

export const userSessionQNAResponsesApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getUserSessionQNAResponses: builder.query({
            query: ({
                userId = "",
                userOrgId = "",
                trailId = "",
                sessionId = "",
                sessionQNASetId = "",
                sessionQNAQuestionId = "",
                responseRole = "",
            } = {}) => ({
                url: '/userSessionQNAResponses/getUserSessionQNAResponses',
                params: {
                    ...(userId ? { userId } : {}),
                    ...(userOrgId ? { userOrgId } : {}),
                    ...(trailId ? { trailId } : {}),
                    ...(sessionId ? { sessionId } : {}),
                    ...(sessionQNASetId ? { sessionQNASetId } : {}),
                    ...(sessionQNAQuestionId ? { sessionQNAQuestionId } : {}),
                    ...(responseRole ? { responseRole } : {}),
                },
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                }
            }),
            transformResponse: responseData => {
                const loadedUserSessionQNAResponses = responseData.map(response => {
                    response.id = response._id
                    return response
                })
                return userSessionQNAResponsesAdapter.setAll(
                    initialState,
                    loadedUserSessionQNAResponses
                )
            },
            providesTags: (result, error, arg) => {
                if (result?.ids) {
                    return [
                        { type: 'UserSessionQNAResponse', id: 'LIST' },
                        ...result.ids.map(id => ({
                            type: 'UserSessionQNAResponse',
                            id,
                        }))
                    ]
                } else {
                    return [{ type: 'UserSessionQNAResponse', id: 'LIST' }]
                }
            },
        }),

        createNewUserSessionQNAResponse: builder.mutation({
            query: initialResponse => ({
                url: '/userSessionQNAResponses/createNewUserSessionQNAResponse',
                method: 'POST',
                body: { ...initialResponse }
            }),
            invalidatesTags: [
                { type: 'UserSessionQNAResponse', id: 'LIST' }
            ]
        }),

        updateUserSessionQNAResponse: builder.mutation({
            query: initialResponse => ({
                url: '/userSessionQNAResponses/updateUserSessionQNAResponse',
                method: 'PATCH',
                body: { ...initialResponse }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'UserSessionQNAResponse', id: arg.id },
                { type: 'UserSessionQNAResponse', id: 'LIST' }
            ],
        }),

        deleteUserSessionQNAResponse: builder.mutation({
            query: ({ id, deleteMode = "soft" }) => ({
                url: '/userSessionQNAResponses/deleteUserSessionQNAResponse',
                method: 'DELETE',
                body: { id, deleteMode }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'UserSessionQNAResponse', id: arg.id },
                { type: 'UserSessionQNAResponse', id: 'LIST' }
            ]
        }),
    }),
})

export const {
    useGetUserSessionQNAResponsesQuery,
    useCreateNewUserSessionQNAResponseMutation,
    useUpdateUserSessionQNAResponseMutation,
    useDeleteUserSessionQNAResponseMutation,
} = userSessionQNAResponsesApiSlice

export const selectUserSessionQNAResponseResult =
    userSessionQNAResponsesApiSlice.endpoints.getUserSessionQNAResponses.select()

const selectUserSessionQNAResponsesData = createSelector(
    selectUserSessionQNAResponseResult,
    userSessionQNAResponseResult => userSessionQNAResponseResult.data
)

export const {
    selectAll: selectAllUserSessionQNAResponses,
    selectById: selectUserSessionQNAResponseById,
    selectIds: selectUserSessionQNAResponseIds,
} = userSessionQNAResponsesAdapter.getSelectors(
    state => selectUserSessionQNAResponsesData(state) ?? initialState
)